<template>
	<div class="cog-input">
		<div class="cog-upload" :class="{'is-read-only': readonly}" @paste="pasteFile">
			<el-upload
				ref="upload"
				:show-file-list="false"
				:http-request="uploadFile"
				:on-preview="previewFile"
				:before-upload="beforeUpload"
				:file-list="initialFiles"
				:disabled="readonly"
				:limit="maxFiles"
				:on-exceed="limitExceeded"
				:multiple="!maxFiles || maxFiles > 1"
				:drag="!readonly && !limitReached"
				:aria-required="isRequired ? true : undefined"
				:input-id="fullId"
				:aria-invalid="hasError ? true : undefined"
				:aria-describedby="ariaDescriptions"
				list-type="picture"
				action=""
			>
				<template v-if="!readonly && !limitReached">
					<div @click="handleUploadClicks">
						<c-button ref="uploadButton" class="cog-button--secondary cog-upload__upload-button" @click="uploadClicked">
							{{ $resource('fileupload-default-upload-button-text') }}
						</c-button>
						<span class="cog-upload__drag-text" @click.stop>{{ $resource('fileupload-dropzone-message') }}</span>
					</div>
				</template>
			</el-upload>

			<transition-group v-if="uploadFiles.length" name="cog-upload__list" tag="ul" class="cog-upload__list" @click.stop>
				<li v-for="(file, i) in uploadFiles" :key="key(file.uid)" ref="uploadItems">
					<img v-if="hasThumbnail(file)" class="cog-upload__thumbnail" :src="getThumbnail(file)" onerror="this.className += ' error'" />
					<c-file-icon :key="'icon-'+i" :extension="getExtension(file)" />
					<div class="cog-upload__file-info">
						<a :key="'anchor-'+file.uid" class="cog-upload__link" :href="file.url" target="_blank" @click.prevent="tryPreviewFile(file, $event)">{{ file.name }}</a>
						<div class="cog-upload__file-size">{{ getDisplayFileSize(file) }}</div>
					</div>
					<template v-if="file.percentage === 100 || file.status === 'success'">
						<c-button :key="'download-button-'+file.uid" class="cog-upload__download-button" no-style :title="getDownloadText(file)" @click="tryDownloadFile(file, $event)">
							<i-download />
						</c-button>
						<c-button v-if="!readonly" :key="'remove-button-'+file.uid" class="cog-upload__remove-button" no-style :title="getRemoveText(file)" @click="removeFile(file.uid)">
							<i-ex />
						</c-button>
					</template>
					<template v-else>
						<progress :key="'progress-'+file.uid" max="100" :value="file.percentage"></progress>
						<c-button class="cog-upload__remove-button" no-style :title="getRemoveText(file)" @click="cancelUpload(file.uid)">
							<i-ex />
						</c-button>
					</template>
				</li>
			</transition-group>
			<transition	name="cog-error-message">
				<div v-if="error && !readonly" class="cog-error-message">{{ error }}</div>
			</transition>
		</div>
	</div>
</template>

<script>
	import Vue from 'vue';
	import { Upload } from '@cognitoforms/element-ui';
	import recordFocus from '../mixins/record-focus';
	import CButton from './Button.vue';
	import CFileIcon from './FileIcon.vue';
	import IEx from './../assets/ex-outline.svg';
	import IDownload from './../assets/download.svg';
	import { formatFileSize } from '@cognitoforms/utils/utilities';
	import { Deferred } from '../util/deferred';
	import { RequestCancelledError } from 'src/web-api/base-service';
	import { FormEvents } from 'src/framework/public/events';
	import { FormEvent } from 'src/framework/eventing/form-event';
	import debounce from 'src/util/debounce';
	import { exceedsPreviewSizeCap, getInlineViewerUrl, getViewerType } from '@cognitoforms/file-viewer/src/file-viewer.types';

	Vue.use(Upload);

	function getMegabytes(file) {
		return file.size / 1024 / 1024;
	}

	const rasterImageTypes = ['png', 'gif', 'jpg', 'jpeg', 'tif', 'tiff', 'bmp'];

	export default {
		name: 'CUpload',
		components: { IEx, IDownload, CButton, CFileIcon },
		mixins: [recordFocus],
		inject: {
			$resource: { default: () => ({}) },
			formEvents: { default: () => ({}) },
			flags: { default: () => ({}) },
			fieldContext: { default: () => () => ({ hasError: false, isRequired: false }) },
			form: { default: null }
		},
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			encrypt: Boolean,
			fileService: { type: Object, required: true },
			value: { type: [Object, Array], default: () => [] },
			allowedTypes: { type: Array, default: undefined },
			bannedTypes: { type: Array, default: () => [] },
			maxSize: { type: Number, default: undefined },
			maxFiles: { type: Number, default: undefined },
			id: { type: String, required: true },
			readonly: Boolean
		},
		data() {
			const internalValue = !this.value && this.multiple ? [] : this.value;
			return {
				updating: false,
				initialFiles: [],
				internalValue,
				error: '',
				uploadRef: null,
				cancellationMap: {},
				uidMap: {},
				debouncedReset: debounce(this.reset),
				resetPromise: new Deferred()
			};
		},
		computed: {
			/**
			 * The list of files for the file input.
			 */
			uploadFiles() {
				this.$nextTick(() => this.resetPromise.resolve());
				return this.uploadRef ? this.uploadRef.uploadFiles : [];
			},
			limitReached() {
				return this.internalValue.length === this.maxFiles;
			},
			isList() {
				return Array.isArray(this.internalValue);
			},
			lowerCaseAllowedTypes() {
				// Necessary since the getExtension method always returns a lowercase string
				return this.allowedTypes ? this.allowedTypes.map(t => t.toLowerCase()) : [];
			},
			hasError() {
				return this.fieldContext().hasError;
			},
			fullId() {
				return this.id ? 'cog-' + this.id : null;
			},
			isRequired() {
				return this.fieldContext().isRequired;
			},
			hasHelptext() {
				return this.fieldContext().hasHelptext;
			},
			ariaDescriptions() {
				const ids = [
					this.hasError ? `${this.fullId}-error` : null,
					this.hasHelptext ? `${this.fullId}-helptext` : null
				].filter(Boolean);
				return ids.length ? ids.join(' ') : undefined;
			}
		},
		watch: {
			value(val) {
				this.internalValue = val;

				// Reset the file list if files are changed from *outside* the component.
				if (!this.updating)
					this.reset(false);
			}
		},
		async mounted() {
			this.$el.querySelector('.el-upload').setAttribute('tabindex', '-1');

			if (this.form && typeof this.form.registerUpload === 'function' && this.form.canHostLocalFileViewer)
				this.form.registerUpload(this.id, this);

			if (this.formEvents)
				this.formEvents.on(FormEvents.ResetEntry, this.debouncedReset);

			await this.reset();
			this.uploadRef = this.$refs.upload;
		},
		destroyed() {
			if (this.form && typeof this.form.unregisterUpload === 'function')
				this.form.unregisterUpload(this.id);

			this.formEvents.off(FormEvents.ResetEntry, this.debouncedReset);
		},
		methods: {
			async reset(cancelUploads = true) {
				if (!this.resetPromise.pending)
					this.resetPromise = new Deferred();
				const fileList = Array.isArray(this.internalValue) ? this.internalValue : [this.internalValue];
				this.initialFiles = await Promise.all(fileList.map(this.fileDataToFile.bind(this)));
				if (cancelUploads) {
					this.uploadFiles.forEach(file => this.cancelUpload(file.uid));
					this.$nextTick(() => this.resetPromise.resolve());
				}
				return this.resetPromise;
			},
			getDisplayFileSize(file) {
				return formatFileSize(file.size);
			},
			uploadClicked() {
				this.error = '';
			},
			getDownloadText(file) {
				return this.$resource('fileupload-download-text') + ' ' + file.name;
			},
			getRemoveText(file) {
				return this.$resource('remove-item').replace('{item}', file.name);
			},
			async fileDataToFile(file) {
				return {
					uid: file.Id,
					url: await this.fileService.getDownloadUrl(file),
					name: file.Name,
					size: file.Size
				};
			},
			getFileRef(file) {
				return this.isList ? this.internalValue.find(f => f.Id === file.uid) : this.internalValue;
			},
			hasThumbnail(file) {
				const fileDataRef = this.getFileRef(file);
				return fileDataRef && fileDataRef.Id && rasterImageTypes.includes(this.getExtension(file));
			},
			getThumbnail(file) {
				return this.fileService.getThumbnailUrl(this.getFileRef(file));
			},
			getExtension(file) {
				return file.name ? file.name.split('.').pop().toLowerCase() : '???';
			},
			getFileViewerFieldLabel() {
				return this.fieldContext().label || this.id;
			},
			isFileViewerVisible() {
				return !!(this.$el && this.$el.getClientRects && this.$el.getClientRects().length);
			},
			async getFileViewerFiles() {
				const files = (Array.isArray(this.internalValue) ? this.internalValue : [this.internalValue]).filter(file => file && file.Id);

				return Promise.all(files.map(async file => {
					const downloadUrl = await this.fileService.getDownloadUrl(file);
					return {
						Id: file.Id,
						Name: file.Name || 'File',
						Size: file.Size || 0,
						ContentType: file.ContentType || '',
						downloadUrl,
						viewerUrl: getInlineViewerUrl(downloadUrl)
					};
				}));
			},
			// https://github.com/ElemeFE/element/blob/dev/packages/upload/src/upload.vue
			uploadFile(options) {
				const cancelPromise = new Deferred();
				this.cancellationMap[options.file.uid] = cancelPromise.resolve.bind(cancelPromise);
				return this.fileService.upload(options.file, { onProgress: options.onProgress, encrypt: this.encrypt, cancelPromise: cancelPromise.promise })
					.then(async fileData => {
						// Make sure Element's file object has correct information
						Object.assign(this.uploadRef.uploadFiles.find(f => f.uid === options.file.uid), await this.fileDataToFile(fileData));
						this.uidMap[fileData.Id] = options.file.uid;
						this.uploaded(fileData);
						delete this.cancellationMap[options.file.uid];
					}, e => {
						this.uploadError(options.file, e.request && e.request.status === 415 ? this.$resource('fileupload-excluded-file-type-message') : null, e instanceof RequestCancelledError);
					});
			},
			uploaded(fileDataRef) {
				this.updating = true;
				this.$emit('change', this.isList ? this.insertInDisplayOrder(fileDataRef) : fileDataRef);
				this.$emit('file-uploaded', fileDataRef);
				this.$nextTick(() => { this.updating = false; });
				this.notifyFileViewerDock();
			},
			insertInDisplayOrder(fileDataRef) {
				const displayedFiles = this.uploadFiles;
				const displayIndexOf = file => displayedFiles.findIndex(f => f.uid === file.Id);
				const uploadedIndex = displayIndexOf(fileDataRef);
				if (uploadedIndex < 0)
					return this.internalValue.concat(fileDataRef);

				const insertAt = this.internalValue.findIndex(file => displayIndexOf(file) > uploadedIndex);
				if (insertAt < 0)
					return this.internalValue.concat(fileDataRef);

				return [...this.internalValue.slice(0, insertAt), fileDataRef, ...this.internalValue.slice(insertAt)];
			},
			uploadError(file, msg, wasCancelled) {
				const idx = this.uploadRef.uploadFiles.findIndex(f => f.uid === file.uid);
				if (idx >= 0)
					this.uploadRef.uploadFiles.splice(idx, 1);

				if (!wasCancelled)
					this.error = msg || this.$resource('fileupload-failed-upload-message', { fileName: file.name });
			},
			cancelUpload(targetId) {
				const cancel = this.cancellationMap[targetId];
				if (cancel) {
					cancel();
					delete this.cancellationMap[targetId];
				}
			},
			removeFile(targetId) {
				this.updating = true;
				this.error = '';

				this.recordFocus();
				// Either emit null if using a single file, or the list excluding the deleted file
				this.$emit('change', this.isList ? this.internalValue.filter(f => f.Id !== targetId) : null);

				// Keep uploadFiles in sync with internalValue
				this.uploadRef.uploadFiles.splice(this.uploadRef.uploadFiles.findIndex(f => f.uid === targetId), 1);
				this.$nextTick(() => { this.updating = false; });
				this.notifyFileViewerDock();
			},
			/**
			 * Handle case when multiple files are selected/dragged at once, and the count exceeds the limit.
			 * el-upload blocks the entire batch of files, so we just want to display an error message explaining
			 * what happened.
			 */
			limitExceeded(files) {
				const resourceKey = this.maxFiles === 1
					? 'fileupload-max-file-count-exceeded-message-singular'
					: 'fileupload-max-file-count-exceeded-message';
				this.error = this.$resource(resourceKey, { maxFileCount: this.maxFiles });
			},
			async downloadFile(file) {
				if (!this.fileService)
					return;

				const fileDataRef = this.getFileRef(file);
				this.fileService.downloadFile(fileDataRef);
			},
			async previewFile(file) {
				if (!this.fileService)
					return;

				const fileDataRef = this.getFileRef(file);
				const fileId = (fileDataRef && fileDataRef.Id) || file.uid;

				if (fileId) {
					if (this.form && typeof this.form.openFileViewer === 'function') {
						let opened = false;
						try {
							opened = await this.form.openFileViewer(fileId, { fieldId: this.id });
						}
						catch (e) {
							console.error(e);
						}

						if (opened)
							return;
					}

					if (this.formEvents && typeof this.formEvents.emit === 'function') {
						const notHandled = await this.formEvents.emit(new FormEvent(FormEvents.OpenFileViewer, {
							fileId,
							fileName: (fileDataRef && fileDataRef.Name) || file.name,
							fieldId: this.id
						}));
						if (!notHandled)
							return;
					}
				}

				const viewerType = getViewerType({ Name: file.name, ContentType: fileDataRef && fileDataRef.ContentType });
				let downloadUrl = this.fileService.getDownloadUrl(fileDataRef);
				let shouldPreview = false;

				if (viewerType === 'document' && !exceedsPreviewSizeCap(file.size))
					shouldPreview = true;
				else if (viewerType === 'pdf' || viewerType === 'image')
					downloadUrl = getInlineViewerUrl(downloadUrl);

				if (shouldPreview)
					downloadUrl = await this.fileService.getPreviewUrl(fileDataRef);

				window.open(downloadUrl, '_blank');
			},
			notifyFileViewerDock() {
				if (this.form && typeof this.form.refreshFileViewerDock === 'function')
					this.$nextTick(() => this.form.refreshFileViewerDock());
			},
			beforeUpload(file) {
				this.error = '';

				// File type blacklisted
				if (this.bannedTypes.includes(this.getExtension(file))) {
					this.error = this.$resource('fileupload-excluded-file-type-message');
					return false;
				}

				// File type not whitelisted
				if (this.lowerCaseAllowedTypes.length !== 0 && !this.lowerCaseAllowedTypes.includes(this.getExtension(file))) {
					this.error = this.$resource('fileupload-invalid-file-type-message', { allowedTypes: this.allowedTypes.join(', ') });
					return false;
				}

				// File too big
				if (this.maxSize && getMegabytes(file) > this.maxSize) {
					this.error = this.$resource('fileupload-max-size-exceeded-message', { maxSize: this.maxSize });
					return false;
				}

				// File too small
				if (file.size === 0) {
					this.error = this.$resource('fileupload-zero-byte-file-message');
					return false;
				}
			},
			/**
			 * Using this map allows us to reuse the file list item when the upload completes.
			 * Without the mapping of FileData Id => file uid, the vue key would change,
			 * causing the DOM to rerender the list item even though it still corresponds
			 * to the same file.
			 */
			key(id) {
				return this.uidMap[id] || id;
			},
			pasteFile(event) {
				if (this.$el.contains(document.activeElement) && !this.readonly) {
					const items = (event.clipboardData || event.originalEvent.clipboardData).items;
					for (const item of items) {
						if (item.kind === 'file') {
							const file = item.getAsFile();
							this.$refs.upload.handleStart(file);
							this.$refs.upload.$refs['upload-inner'].upload(file);
						}
					}
				}
			},
			handleUploadClicks(event) {
				if (!this.$refs.uploadButton.$el.contains(event.target))
					event.stopPropagation();
			},
			tryDownloadFile(file, event) {
				if (this.flags.fileDownloadsDisabled) {
					event.stopPropagation();
					this.formEvents.emit(new FormEvent(FormEvents.TryDownloadOverLimit));
				}
				else {
					this.downloadFile(file);
				}
			},
			tryPreviewFile(file, event) {
				if (this.flags.fileDownloadsDisabled) {
					event.stopPropagation();
					this.formEvents.emit(new FormEvent(FormEvents.TryDownloadOverLimit));
				}
				else {
					this.previewFile(file);
				}
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-upload {

		.el-upload-dragger {
			container-type: inline-size;

			&.is-dragover > div {
				background: hsla(var(--background-hsl), .1);
				border-color: var(--highlight);
			}

			& > div {
				display: flex;
				align-items: center;
				background: $bgColor;
				border: var(--input__border-width) solid transparent;
				border-radius: calc(var(--border-radius) * .7);
				padding: $gutter-half;
				gap: .5em;

				@container (max-width: 13.5em) {
					flex-wrap: wrap;
				}
			}
		}

		.el-upload__input {
			display: none;
		}

		&:not(.is-read-only) {
			border: hsla(var(--background-hsl), .5) var(--input__border-width) solid;
			border-radius: var(--border-radius);
			padding: 2px;
		}

		&__list {
			margin: $gutter-sixth 0;
			padding-right: $gutter-quarter;
			padding-left: $gutter-quarter;
			list-style: none;
			transition: $speed-quarter;

			&:empty {
				margin: 0;
			}

			li {
				display: flex;
				position: relative;
				align-items: center;
				padding: $gutter-quarter;
				transition: $speed-quarter;

				&:hover {
					background: hsla(var(--background-hsl), .1);
				}
			}

			li + li {
				margin-top: $gutter-sixth;
			}

			.cog-upload__thumbnail,
			.cog-i-file {
				max-width: 1.7em;
				margin-right: .2em;
				font-size: 1.5em;
				transition: $speed-quarter;
			}

			progress {
				position: absolute;
				right: var(--gutter);
				width: calc(45px - 2em);
				height: 3px;
				background-color: hsla(var(--background-hsl), .3);
				border: 0;
				margin-right: 1em;
				-webkit-appearance: none;
				appearance: none;
			}

			progress::-webkit-progress-bar {
				background-color: hsla(var(--background-hsl), .5);
			}

			progress::-webkit-progress-value {
				background-color: var(--highlight);
			}

			progress::-moz-progress-bar {
				background-color: var(--highlight);
			}

			progress::-ms-fill {
				border: 0;
			}
		}

		&__list-enter-active,
		&__list-leave-active {
			overflow: hidden;
		}

		&__list-enter-to,
		&__list-leave {
			max-height: 5em;
			opacity: 1;

			.cog-i-file {
				transform: scale(1);
			}
		}

		&__file-info {
			max-width: calc(100% - 7.5em);
			margin-left: $gutter-quarter;
		}

		&__thumbnail:not(.error) + .cog-i-file {
			display: none;
		}

		&__thumbnail.error {
			display: none;
		}

		@include at-root {

			a.cog-upload__link {
				display: inline-block;
			}
		}

		&__link {
			max-width: 100%;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		&__file-size {
			margin-top: -.35em;
			opacity: .6;
			color: var(--color);
			font-size: var(--small-text);
		}

		&__list button {
			margin-left: auto;
		}

		&__list button + button {
			margin-left: .3em;
		}

		&__list-enter,
		&__list-leave-to {
			max-height: 0;
			padding: 0 !important;
			opacity: 0;

			.cog-i-file {
				// when set at zero caused bug 19283
				transform: scale(.1);
			}
		}

		&__upload-button {
			flex-shrink: 0;
		}
	}

	.cog-i-download {
		fill: transparent;
		stroke: currentcolor;

		&__arrow,
		&__ground {
			transition: $speed-quarter;
		}
	}

	.cog-form:not([data-width~='325']) {

		.el-upload-dragger > div {
			padding: $gutter-quarter;
		}

		.cog-upload__list {
			padding-right: 0;
			padding-left: 0;
		}
	}
}

[lang='el'].cog-form,
[lang='fr'].cog-form,
[lang='hi'].cog-form,
[lang='nl'].cog-form,
[lang='nb'].cog-form {

	.el-upload-dragger {

		& > div {
			@container (max-width: 16em) {
				flex-wrap: wrap;
			}
		}
	}
}

</style>
